import { MachineConfig } from 'xstate'
import { has, merge } from 'lodash'
import { Events, FSMCommonTransitions as TV } from './constants'
import { IS_PRODUCTION } from '../../config'
import { ERROR_QUOTE_CONSTRAINTS, ERROR_QUOTE_VALIDATION, ErrorCode } from '../../constants'
import { isCriticalError } from '@dg-util'
import { ErrorEventType, FSMContextType, StatePath } from '../../types/interface/fsm.interface'

export type ContextType = FSMContextType
export type EventType = {
  type: Events
  isVerify?: boolean
  errorType?: ErrorEventType
  state?: StatePath
  backState?: StatePath
  flowName?: string
}
// v5 dropped the `StateSchema` generic; states are inferred from the config itself.
export type MachineConfigType = MachineConfig<ContextType, EventType>
export type StatesConfigType = NonNullable<MachineConfigType['states']>

// FSM states that available only on DEV, LOCAL, ACC environments
const FSMStandards: MachineConfigType = !IS_PRODUCTION
  ? {
      states: {
        [TV.STANDARDS]: {
          on: {
            [Events.BACK]: TV.AUTH,
          },
        },
        [TV.PLAYGROUND]: {
          on: {
            [Events.BACK]: TV.AUTH,
          },
        },
      },
      // Root-level `on` targets need a leading dot on v5; a bare name there is read as a
      // sibling of the root node and is rejected at `createMachine` time.
      on: {
        [Events.STANDARDS]: {
          target: `.${TV.STANDARDS}`,
          actions: 'setBackPath',
        },
        [Events.PLAYGROUND]: {
          target: `.${TV.PLAYGROUND}`,
          actions: 'setBackPath',
        },
      },
    }
  : {}

const FSMConfigCommon: MachineConfigType = {
  states: {
    [TV.ERROR]: {
      on: {
        [Events.BACK]: [
          // General shared critical error back navigation handling
          {
            target: TV.AUTH,
            actions: ['signOut'],
            // `errorType` is captured into context by `setBackPath` when the ERROR event fires.
            // v4 read it off `meta.state.event`, which v5 removed — and which was the only
            // source that survived a bare `BACK` from the browser back button.
            guard: ({ context }) => {
              const errorType = context.errorType

              const isCriticalErrorType = [
                ERROR_QUOTE_CONSTRAINTS,
                ERROR_QUOTE_VALIDATION,
                ErrorCode.ERROR_CODE_NO_QUOTE,
              ].includes(errorType as string)
              const isCriticalErrorCode = isCriticalError(
                (errorType as ErrorEventType)?.code as ErrorCode
              )

              return isCriticalErrorType || isCriticalErrorCode
            },
          },
          {
            target: TV.AUTH,
            actions: ['signOut'],
            guard: ({ context }) => has(context.backPath, TV.AUTH),
          },
          {
            target: TV.QB_CHANGE,
            guard: ({ context }) => context.backPath === TV.QB_CHANGE,
          },
          {
            target: TV.QB_VERIFY,
            guard: ({ context }) => context.backPath === TV.QB_VERIFY,
          },
          {
            target: TV.QB_PURCHASED,
            guard: ({ context }) => context.backPath === TV.QB_PURCHASED,
          },
        ],
      },
    },
  },
  on: {
    [Events.ERROR]: {
      target: `.${TV.ERROR}`,
      actions: 'setBackPath',
    },
    [Events.AUTH_ERROR]: {
      target: `.${TV.AUTH}`,
      actions: ['setMessageQueueEvent', 'signOut'],
    },
    [Events.AUTH]: {
      target: `.${TV.AUTH}`,
      actions: ['setMessageQueueEvent', 'signOut'],
    },
    [Events.SESSION_EXPIRED]: {
      target: `.${TV.AUTH}`,
      actions: ['setMessageQueueEvent', 'signOut'],
    },
  },
}

export default merge(FSMStandards, FSMConfigCommon)
